'use client'

import { type AbstractIntlMessages, NextIntlClientProvider } from 'next-intl'
import { createContext, type ReactNode, use, useMemo, useState } from 'react'

import {
  isSameCatalog,
  type MessageTree,
  overlayCatalog,
} from '@/lib/i18n/catalog-merge'
import type { Locale } from '@/lib/i18n/config'
import { LocaleProvider } from './locale-provider'

/** The catalogs every `MessagesProvider` above this point provides. */
const InheritedMessagesContext = createContext<AbstractIntlMessages | null>(
  null
)

/**
 * The catalogs the `ClientMessages` above a component provide, or `null`
 * outside every one. For code that must render without a provider (an error
 * boundary), where `useTranslations` would throw.
 */
export function useInheritedMessages(): AbstractIntlMessages | null {
  return use(InheritedMessagesContext)
}

/**
 * `messages`, or the catalog an earlier render received when it holds the
 * same messages: a refresh (`router.refresh()`, a revalidating Server
 * Action) sends the server's catalogs again as new objects, and a new
 * object would re-render every component that translates below.
 */
function useSameCatalog(messages: AbstractIntlMessages): AbstractIntlMessages {
  const [held, setHeld] = useState(messages)
  if (held === messages || isSameCatalog(held, messages)) {
    return held
  }
  setHeld(messages)
  return messages
}

/**
 * next-intl's client provider, adding its catalogs to those of the
 * providers above it instead of replacing them: a page's `ClientMessages`
 * inside the app shell keeps the shell's catalogs, and one that gives part
 * of a namespace keeps the rest of it (`overlayCatalog`). Rendered by
 * `ClientMessages`, which picks the namespaces on the server, and by
 * `ProductCatalogs`. The merge keeps its identity while the inherited
 * catalogs and the content of `messages` stay the same, so a refresh
 * re-renders no consumer through it. It also sets `useAppLocale`, so a root
 * that gives only messages (embed, domain share, unsubscribe, OAuth consent)
 * formats numbers and dates in the same language as its text.
 */
export function MessagesProvider({
  locale,
  timeZone,
  messages,
  children,
}: {
  locale: Locale
  /** The server's zone for server-rendered trees; a client-only island omits it. */
  timeZone?: string
  messages: AbstractIntlMessages
  children: ReactNode
}) {
  const inherited = use(InheritedMessagesContext)
  const own = useSameCatalog(messages)
  const merged = useMemo(
    () =>
      inherited === null
        ? own
        : (overlayCatalog(
            inherited as MessageTree,
            own as MessageTree
          ) as AbstractIntlMessages),
    [inherited, own]
  )
  return (
    <InheritedMessagesContext value={merged}>
      <NextIntlClientProvider
        locale={locale}
        timeZone={timeZone}
        messages={merged}
      >
        <LocaleProvider locale={locale}>{children}</LocaleProvider>
      </NextIntlClientProvider>
    </InheritedMessagesContext>
  )
}
